import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UIAction } from '@/components/ui/Action';
import { Typography } from '@/components/ui/Typography';
import { UIButton } from '@/components/ui/Button';

import { NotificationBar } from '@/components/layout/NotificationBar';

// import { cancelLastProductRemoved } from '@/features/sales/salesProductsSlice';
// import { useAppDispatch } from '@/app/store';
import { useSelector } from '@/hooks/useSelector';
import {
  StyledProductItemWrapper,
  ProductItemWrapper,
} from '@/components/layout/ProductItemWrapper';
import { ProductImage } from '@/components/layout/ProductImage';
import { ProductDescription } from '@/components/layout/ProductDescription';
import { ProductAccordion } from '@/components/layout/ProductAccordion';
import { ProductAccordionItem } from '@/components/layout/ProdocutAccordionItem';
import { ProductPrice } from '@/components/layout/ProductPrice';
import { useToggle } from 'react-use';
import { ProductDetailsTransferItems } from '@/api/receive';

//#region types
export interface StyledScannedProductProps {
  $found?: boolean;
  $notFound?: boolean;
  $unexpected?: boolean;
  $sale?: boolean;
}

export interface StyledScannedProductItemProps
  extends StyledScannedProductProps {
  productItem: ProductDetailsTransferItems;
  onClick?: React.MouseEventHandler<HTMLElement>;
  handleAddUnexpected?: (
    product: NonNullable<ProductDetailsTransferItems>,
    epcCode: string
  ) => void;
  handlePrintNotFound?: (
    product: NonNullable<ProductDetailsTransferItems>,
    epcCode: string
  ) => void;
  handleRemoveSaleEpc?: (upcCode: string, epcCode: string) => void;
  handleRemoveSaleProduct?: (upcCode: string, epcCode: string) => void;
  disableActions?: boolean;
  $v2?: boolean;
}
//#endregion

export const ScannedProductItem: FC<StyledScannedProductItemProps> = ({
  $found,
  $notFound,
  $unexpected,
  $sale,
  productItem,
  handleRemoveSaleEpc,
  handleRemoveSaleProduct,
  handleAddUnexpected,
  handlePrintNotFound,
  onClick,
  disableActions,
  $v2 = false,
}): JSX.Element => {
  const {
    brandCode,
    brandDescription,
    commodity,
    colorCode,
    epcCodes,
    modelCode,
    price,
    styleName,
    upcCode,
    imageUrl,
    //@ts-ignore
    currency,
  } = productItem;

  const { t } = useTranslation();
  const { store } = useSelector(state => state.currentStore);

  const [isNotificationBarVisible, setNotificationBarVisible] =
    useState<boolean>(false);

  const [$isAccordionExpanded, toggleAccordion] = useToggle(false);

  const onNotificationCancelClick = (): void => {
    setNotificationBarVisible(false);
    // dispatch(cancelLastProductRemoved());
  };

  const onNotificationBarClose = (): void => {
    setNotificationBarVisible(false);
  };

  const onPrintActionClick = (epcCode: string): void => {
    if (!disableActions) {
      handlePrintNotFound?.(
        productItem as ProductDetailsTransferItems,
        epcCode
      );
    }
  };

  const onUnexpectedActionClick = (epcCode: string): void => {
    if (!disableActions) {
      handleAddUnexpected?.(
        productItem as ProductDetailsTransferItems,
        epcCode
      );
    }
  };

  const onSaleActionClick = (epcCode: string): void => {
    if (!disableActions && upcCode) {
      // @ts-ignore
      if (epcCodes?.epcCodes.length === 1) {
        handleRemoveSaleProduct?.(upcCode, epcCode);
      } else {
        handleRemoveSaleEpc?.(upcCode, epcCode);
      }
    }
  };

  const hasAccordion = epcCodes.epcCodes && epcCodes.epcCodes.length > 1;

  const renderActions = (epcCode: string): JSX.Element => {
    return (
      <>
        {$unexpected && (
          <>
            <UIAction
              label={t('remove')}
              icon="delete"
              disabled={disableActions}
              onClick={(): void => {}}
            />
            <UIAction
              label={t('add')}
              icon="add"
              disabled={disableActions}
              onClick={(): void => onUnexpectedActionClick(epcCode)}
            />
          </>
        )}
        {$notFound && (
          <UIAction
            label={t('print')}
            icon="print"
            disabled={
              store?.printers === null ||
              store?.printers?.length === 0 ||
              disableActions
            }
            onClick={(): void => onPrintActionClick(epcCode)}
          />
        )}
        {$sale && (
          <UIAction
            label={t('remove')}
            icon="delete"
            disabled={disableActions}
            onClick={(): void => onSaleActionClick(epcCode)}
            isVisible
          />
        )}
      </>
    );
  };

  return (
    <>
      <StyledProductItemWrapper>
        <ProductItemWrapper
          $found={$found}
          $sale={$sale}
          $notFound={$notFound}
          $unexpected={$unexpected}
          $withAccordion={hasAccordion}
        >
          <ProductImage
            imageUrl={imageUrl}
            onClick={onClick}
            colorCode={colorCode}
            modelCode={modelCode}
            brandCode={brandCode}
            $v2={$v2 && hasAccordion}
            onClickAccordion={toggleAccordion}
            $isAccordionExpanded={$isAccordionExpanded}
          />
          <ProductDescription>
            <Typography size="lg" font="heavy" as="span">
              {brandCode} - {brandDescription} - {styleName}
            </Typography>
            <Typography size="md" font="book" as="p">
              {[upcCode, modelCode, commodity].filter(el => el).join(' - ')}
              {epcCodes.epcCodes && epcCodes.epcCodes.length === 1 && (
                <span className="epc-code"> - {epcCodes.epcCodes[0]}</span>
              )}
            </Typography>
          </ProductDescription>
          <ProductPrice
            price={price ? String(price) : undefined}
            currency={currency}
          >
            {$v2 && (
              <Typography font="light">
                {t('itemQuantity', {
                  itemQuantity: epcCodes?.epcCodes?.length,
                })}
              </Typography>
            )}
            {Array.isArray(epcCodes) && epcCodes.length === 1
              ? renderActions(epcCodes[0])
              : epcCodes.epcCodes &&
                epcCodes.epcCodes.length === 1 &&
                renderActions(epcCodes.epcCodes[0])}
          </ProductPrice>
        </ProductItemWrapper>
        {hasAccordion && (
          <ProductAccordion
            items={
              Array.isArray(epcCodes)
                ? `${epcCodes!.length} ${t('items')}`
                : `${epcCodes!.epcCodes!.length} ${t('items')}`
            }
            $v2={$v2}
            expanded={$isAccordionExpanded}
            toggleAccordion={toggleAccordion}
          >
            {Array.isArray(epcCodes) ? (
              <>
                {epcCodes!.map((epcCode: string, i: number) => (
                  <ProductAccordionItem
                    key={`${epcCode}-${i}-code`}
                    epcCode={epcCode}
                  >
                    {renderActions(epcCode)}
                  </ProductAccordionItem>
                ))}
              </>
            ) : (
              <>
                {epcCodes!.epcCodes!.map((epcCode: string, i: number) => (
                  <ProductAccordionItem
                    key={`${epcCode}-${i}-code`}
                    epcCode={epcCode}
                  >
                    {renderActions(epcCode)}
                  </ProductAccordionItem>
                ))}
              </>
            )}
          </ProductAccordion>
        )}
      </StyledProductItemWrapper>

      <NotificationBar
        cta={
          <UIButton
            label={t('cancel')}
            outlined
            disabled={false}
            onClick={onNotificationCancelClick}
          />
        }
        message={t('sale.removeArticle')}
        vertical="bottom"
        horizontal="center"
        open={isNotificationBarVisible}
        autoHideDuration={6000}
        onClose={onNotificationBarClose}
      />
    </>
  );
};

export default ScannedProductItem;
